import React, { useState, useEffect, useRef } from 'react'
import { Modal, Button, Form, Row, Col } from 'react-bootstrap'
import { IFeed_Input, IFeed_Input_Rule, IFeed_Input_Link, IFeed_Input_UpdateInterval_Timezone } from './types'
import FeedRulesForm from './feed-rules-form'
import FeedLinksForm from './feed-links-form'
import { getUserTimezoneOffset } from '../../../_utils'

interface FeedModalProps {
    show: boolean
    onClose: () => void
    onSave: (input: IFeed_Input) => Promise<void>
    onRemove?: () => void
    showRemove?: boolean
    feedInput?: IFeed_Input
}

const FeedInputModal: React.FC<FeedModalProps> = ({ show, onClose, onSave, onRemove, showRemove, feedInput }) => {
    const [localInput, setLocalInput] = useState<IFeed_Input>({
        name: '',
        rules: [],
        updateInterval: {
            frequency: 'daily',
            hour: '00',
            timezoneOffset: getUserTimezoneOffset(), 
        },
        links: [{ name: '', link: '', id: '', kind: 'csv', dataSourceType:'url' }],
    })

    const [formSubmitted, setFormSubmitted] = useState(false)
    const formRef = useRef<HTMLFormElement>(null)
    const generateId = () => Math.random().toString(36).substring(2, 8)

    useEffect(() => {
        if (feedInput) {
            setLocalInput(feedInput)
        } else {
            setLocalInput({
                name: '',
                rules: [],
                updateInterval: {
                    frequency: 'daily',
                    hour: '00',
                    timezoneOffset: getUserTimezoneOffset(),
                },
                links: [{ name: '', link: '', id: generateId(), kind: 'csv', dataSourceType:'url' }],
            })
        }
    }, [feedInput, show])

    const handleSave = async () => {
        if (formRef.current?.checkValidity()) {
            await onSave(localInput)
            onClose()
        } else {
            formRef.current?.reportValidity()
        }
    }

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        setFormSubmitted(true)
        await handleSave()
    }

    const handleRulesChange = (updatedRules: IFeed_Input_Rule[]) => {
        setLocalInput({ ...localInput, rules: updatedRules })
    }

    const handleLinksChange = (updatedLinks: IFeed_Input_Link[]) => {
        setLocalInput({ ...localInput, links: updatedLinks })
    }

    const handleAddLink = () => {
        setLocalInput({
            ...localInput,
            links: [...localInput.links, { name: '', link: '', id: generateId(), kind: 'csv',dataSourceType:'url' }],
        })
    }

    useEffect(() => {
        const ensureOneLink = () => {
            setLocalInput((prevInput) => {
                if (prevInput.links.length === 0) {
                    return { ...prevInput, links: [{ name: '', link: '', id: generateId(), kind: 'csv',dataSourceType:'url' }] }
                }
                return prevInput
            })
        }
        ensureOneLink()
    }, [localInput.links])

    const getTimezoneOffsetOptions = () => {
        const offsets = []
        for (let i = -12; i <= 14; i++) offsets.push(i)
        
        return offsets
    }

    const timezoneOptions = getTimezoneOffsetOptions()

    const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const daysOfMonth = Array.from({ length: 31 }, (_, i) => (i + 1).toString())

    return (
        <Modal
            show={show}
            onHide={onClose}
            size='xl'
            backdrop="static" 
            keyboard={false}
        >
            <Modal.Header closeButton>
                <Modal.Title>Edit Feed Input</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form ref={formRef} noValidate onSubmit={handleSubmit}>
                    <Form.Group controlId="name">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={localInput.name || ''}
                            onChange={(e) => setLocalInput({ ...localInput, name: e.target.value })}
                            required
                            isInvalid={formSubmitted && !localInput.name}
                        />
                        <Form.Control.Feedback type="invalid">
                            Please provide a name.
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Row className="mb-3">
                        <Col sm={3}>
                            <Form.Group controlId="frequency">
                                <Form.Label>Frequency</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={localInput.updateInterval.frequency || 'daily'}
                                    onChange={(e) => setLocalInput({
                                        ...localInput,
                                        updateInterval: { ...localInput.updateInterval, frequency: e.target.value as any }
                                    })}
                                    required
                                >
                                    <option value="hourly">Hourly</option>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        {localInput.updateInterval.frequency !== 'hourly' &&
                        <>
                                <Col sm={3}>
                                    <Form.Group controlId="hour">
                                        <Form.Label>Hour</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={localInput.updateInterval.hour || '00'}
                                            onChange={(e) => setLocalInput({
                                                ...localInput,
                                                updateInterval: { ...localInput.updateInterval, hour: e.target.value } as IFeed_Input_UpdateInterval_Timezone
                                            })}
                                            required
                                        >
                                            {hours.map((h) => (
                                                <option key={h} value={h}>
                                                    {h}:00
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>

                                <Col sm={3}>
                                    <Form.Group controlId="timezone">
                                        <Form.Label>Timezone</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={localInput.updateInterval.timezoneOffset !== undefined ? localInput.updateInterval.timezoneOffset.toString() : getUserTimezoneOffset().toString()}
                                            onChange={(e) => setLocalInput({
                                                ...localInput,
                                                updateInterval: { ...localInput.updateInterval, timezoneOffset: parseInt(e.target.value) } as IFeed_Input_UpdateInterval_Timezone
                                            })}
                                            required
                                        >
                                            {timezoneOptions.map((tz, index) => (
                                                <option key={index} value={`${tz}`}>
                                                    GMT {tz >= 0 ? `+${tz}` : `${tz}`}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>

                            {localInput.updateInterval.frequency === 'weekly' && (
                                    <Col sm={3}>
                                        <Form.Group controlId="dayOfWeek">
                                            <Form.Label>Day of Week</Form.Label>
                                            <Form.Control
                                                as="select"
                                                value={localInput.updateInterval.dayOfWeek || ''}
                                                onChange={(e) => setLocalInput({
                                                    ...localInput,
                                                    updateInterval: { ...localInput.updateInterval, dayOfWeek: e.target.value } as IFeed_Input_UpdateInterval_Timezone
                                                })}
                                            >
                                                {daysOfWeek.map((day, index) => (
                                                    <option key={index} value={day}>
                                                        {day}
                                                    </option>
                                                ))}
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                            )}

                            {localInput.updateInterval.frequency === 'monthly' && (
                                    <Col sm={3}>
                                        <Form.Group controlId="dayOfMonth">
                                            <Form.Label>Day of Month</Form.Label>
                                            <Form.Control
                                                as="select"
                                                value={localInput.updateInterval.dayOfMonth || ''}
                                                onChange={(e) => setLocalInput({
                                                    ...localInput,
                                                    updateInterval: { ...localInput.updateInterval, dayOfMonth: e.target.value } as IFeed_Input_UpdateInterval_Timezone
                                                })}
                                            >
                                                {daysOfMonth.map((day) => (
                                                    <option key={day} value={day}>
                                                        {day}
                                                    </option>
                                                ))}
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                            )}
                        </>
                        }
                    </Row>


                    <FeedLinksForm
                        links={localInput.links}
                        onChange={handleLinksChange}
                        onAddLink={handleAddLink}
                    />
                    
                    <FeedRulesForm
                        rules={localInput.rules}
                        onChange={handleRulesChange}
                    />

                </Form>
            </Modal.Body>
            <Modal.Footer>
                {showRemove && onRemove && (
                    <Button variant="danger" onClick={onRemove} className="btn-sm me-auto">
                        Remove
                    </Button>
                )}
                <Button variant="secondary" onClick={onClose} className="btn-sm">
                    Close
                </Button>
                <Button type='submit' variant="primary" className="btn-sm" onClick={handleSubmit}>
                    Save Changes
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default FeedInputModal
